/**
 * Card anchor + rename plumbing (founder 2026-07-13).
 *
 * Two jobs, one tiny context:
 *  1. ANCHOR — each card's render choke point (Hideable / MetricCard / a dashboard
 *     widget / a profit-model tile) emits `data-card-anchor="<renderId>"` on its
 *     DOM root so the "See it live →" deep-link (?spotlight=<renderId>) can scroll
 *     to and flash it. See lib/spotlight.tsx.
 *  2. RENAME — the same choke point provides its renderId through CardAnchorCtx, so
 *     a nested header (CardHead / CardHeader) can look up the operator's rename
 *     override for THIS card and show it instead of the hardcoded title. Section
 *     wrappers provide their id too, but the server never emits an override for a
 *     section id, so a header inside a section keeps its own title.
 */

import { createContext, useContext } from "react";
import { useCardNameOverride } from "./card-approval.js";

/** The current card's renderId, or null outside any card. */
export const CardAnchorCtx = createContext<string | null>(null);

/**
 * Resolve the title to render: the operator's rename override for the current
 * card (from CardAnchorCtx), else the hardcoded `fallback`. Safe anywhere — with
 * no override (or outside a CardApprovalProvider) it just returns `fallback`.
 */
export function useCardName(fallback: string): string {
  const id = useContext(CardAnchorCtx);
  const override = useCardNameOverride(id);
  return override ?? fallback;
}
